// react
import { useContext, useMemo, useState } from 'react';

// mui
import { Box } from '@mui/material';
import {
	Add as IconAdd,
	Download as IconDownload,
	Store as IconSupplier,
	UploadFile as IconUpload,
} from '@mui/icons-material';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextLoans, ContextProject, ContextSuppliers } from '../../Project/Project.context';

// hooks
import { useProjectLookups } from '../../../../hooks/useProjectLookups';

// router
import { useSearchParams } from 'react-router-dom';

// ui
import { CustomFieldsLink } from '../../../ui/CustomFieldsLink/CustomFieldsLink';
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';
import { BtnSplit } from '../../../ui/BtnSplit/BtnSplit';

// dialogs
import { DialogAddContract } from '../../../dialogs/DialogAddContract/DialogAddContract';
import { DialogAddSupplier } from '../../../dialogs/DialogAddSupplier/DialogAddSupplier';
import { DialogEditSupplier } from '../../../dialogs/DialogEditSupplier/DialogEditSupplier';
import { DialogUpdateEntityStatus } from '../../../dialogs/DialogUpdateEntityStatus/DialogUpdateEntityStatus';
import { DialogUploadDocsEntity } from '../../../dialogs/DialogUploadDocsEntity/DialogUploadDocsEntity';
import {
	DialogBulkUpload,
	v1SupplierSchema,
	downloadTemplate,
} from '../../../dialogs/DialogBulkUpload';

// components
import { SuppliersDataTable } from './SuppliersDataTable';

// types
import { Entity } from '../../../../@types';

export const Suppliers = () => {
	// hooks
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();

	// state for deactivate dialog
	const [deactivateEntity, setDeactivateEntity] = useState<Entity | null>(null);
	const [addContractEntity, setAddContractEntity] = useState<Entity | null>(null);
	const [uploadDocsEntity, setUploadDocsEntity] = useState<Entity | null>(null);

	// context
	const { project } = useContext(ContextProject);
	const { loans } = useContext(ContextLoans);
	const { suppliers, isLoadingSuppliers, paginationSuppliers, refetchSuppliers } =
		useContext(ContextSuppliers);

	// Full project-universe lookup lists for bulk-upload relation resolution.
	// A supplier's parent is an implementing organisation, so pull every such
	// entity for the project (no page limit) rather than the paginated,
	// type-filtered listing context. Loans come from context (fully loaded there).
	const lookups = useProjectLookups({
		projectId: project?.id,
		isEnabled: searchParams.get('dialog') === 'bulkUpload',
		entityTypes: 'BORROWER,INTERMEDIARY,PIU,SUB_PIU',
	});

	// Lookup data for bulk upload relation resolution
	const lookupData = useMemo(
		() => ({
			entities: lookups.entities,
			loans: loans.map((l) => ({
				id: l.id,
				name: l.name || l.code || String(l.id),
				code: l.code,
			})),
		}),
		[lookups.entities, loans]
	);

	// params
	const paramDialog = searchParams.get('dialog');
	const searchQuery = searchParams.get('search') || searchParams.get('q') || '';
	const filterPage = searchParams.get('page') || '1';
	const filterLimit = searchParams.get('limit') || '10';
	const filterOrderBy = searchParams.get('orderBy') || 'created';
	const filterOrder = (searchParams.get('order') || 'DESC') as 'ASC' | 'DESC';

	// Custom field definitions for suppliers
	const customFieldDefs = useMemo(
		() =>
			(project?.customFieldDefinitions || []).find((d) => d.entityType === 'supplier')
				?.definitions || [],
		[project]
	);

	// ==========================================================================
	// URL param handlers (Contracts pattern)
	// ==========================================================================

	const handleSearchChange = (value: string) => {
		setSearchParams((params) => {
			if (value) {
				params.set('search', value);
				params.set('q', value);
			} else {
				params.delete('search');
				params.delete('q');
			}
			params.set('page', '1');
			return params;
		});
	};

	const handlePageChange = (page: number) => {
		setSearchParams((params) => {
			params.set('page', String(page));
			return params;
		});
	};

	const handleRowsPerPageChange = (rowsPerPage: number) => {
		setSearchParams((params) => {
			params.set('limit', String(rowsPerPage));
			params.set('page', '1');
			return params;
		});
	};

	const handleSortChange = (column: string, direction: 'ASC' | 'DESC') => {
		setSearchParams((params) => {
			params.set('orderBy', column);
			params.set('order', direction);
			return params;
		});
	};

	const handleEditSupplier = (entity: { id: number }) => {
		setSearchParams((params) => {
			params.set('dialog', 'editSupplier');
			params.set('dialogSupplier', String(entity.id));
			return params;
		});
	};

	const handleDeactivateSupplier = (entity: Entity) => {
		setDeactivateEntity(entity);
	};

	const handleAddContract = (entity: Entity) => {
		setAddContractEntity(entity);
	};

	const handleUploadDocuments = (entity: Entity) => {
		setUploadDocsEntity(entity);
	};

	return (
		<>
			<HeaderListingPage
				icon={IconSupplier}
				title={t('suppliers')}
					subtitle={t('spendPlan.suppliers.subtitle')}
				variant="contract"
				secondaryBadge={<CustomFieldsLink entity="supplier" />}
				actions={
					<BtnSplit
						actions={[
							{
								children: t('addSupplier'),
								onClick: () => {
									searchParams.set('dialog', 'addSupplier');
									setSearchParams(searchParams);
								},
								startIcon: <IconAdd />,
							},
							{
								children: t('spendPlan.bulkUploadFromFile', 'Bulk Upload from File'),
								onClick: () => {
									searchParams.set('dialog', 'bulkUpload');
									setSearchParams(searchParams);
								},
								startIcon: <IconUpload />,
							},
							{
								children: t('spendPlan.downloadTemplate', 'Download Template'),
								onClick: async () => {
									try {
										await downloadTemplate({ schema: v1SupplierSchema });
									} catch {
										/* ignore */
									}
								},
								startIcon: <IconDownload />,
							},
						]}
					/>
				}
			/>
			<Box sx={{ px: 3, pb: 3, pt: 2 }}>
				<SuppliersDataTable
					suppliers={suppliers}
					isLoading={isLoadingSuppliers}
					searchValue={searchQuery}
					onSearchChange={handleSearchChange}
					orderBy={filterOrderBy}
					order={filterOrder}
					onSortChange={handleSortChange}
					page={parseInt(filterPage)}
					rowsPerPage={parseInt(filterLimit)}
					totalCount={paginationSuppliers?.total || 0}
					onPageChange={handlePageChange}
					onRowsPerPageChange={handleRowsPerPageChange}
					onEditSupplier={handleEditSupplier}
					onDeactivateSupplier={handleDeactivateSupplier}
					onAddContract={handleAddContract}
					onUploadDocuments={handleUploadDocuments}
					customFieldDefinitions={customFieldDefs}
				/>
			</Box>
			<DialogBulkUpload
				isOpen={paramDialog === 'bulkUpload'}
				onClose={() => {
					setSearchParams((params) => {
						params.delete('dialog');
						return params;
					});
				}}
				schema={v1SupplierSchema}
				projectId={project?.id || 0}
				lookupData={lookupData}
				onSuccess={() => {
					refetchSuppliers();
				}}
			/>
			<DialogAddSupplier
				isOpen={paramDialog === 'addSupplier'}
				onClose={() => {
					setSearchParams((params) => {
						params.delete('dialog');
						return params;
					});
				}}
				onSuccess={() => {
					refetchSuppliers();
				}}
			/>
			<DialogEditSupplier
				isOpen={paramDialog === 'editSupplier'}
				onClose={() => {
					setSearchParams((params) => {
						params.delete('dialog');
						params.delete('dialogSupplier');
						return params;
					});
				}}
				onSuccess={() => {
					refetchSuppliers();
				}}
			/>
			{deactivateEntity && (
				<DialogUpdateEntityStatus
					entity={deactivateEntity}
					isOpen={!!deactivateEntity}
					onClose={() => setDeactivateEntity(null)}
					onSuccess={() => {
						setDeactivateEntity(null);
						refetchSuppliers();
					}}
				/>
			)}
			{addContractEntity && (
				<DialogAddContract
					entity={addContractEntity}
					isOpen={!!addContractEntity}
					onClose={() => setAddContractEntity(null)}
					onSuccess={() => {
						setAddContractEntity(null);
						refetchSuppliers();
					}}
				/>
			)}
			{uploadDocsEntity && (
				<DialogUploadDocsEntity
					entity={uploadDocsEntity}
					isOpen={!!uploadDocsEntity}
					onClose={() => setUploadDocsEntity(null)}
					onSuccess={() => {
						setUploadDocsEntity(null);
						refetchSuppliers();
					}}
				/>
			)}
		</>
	);
};
